import { Injectable, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, Subject, filter, firstValueFrom, map, take, takeUntil, tap } from 'rxjs';

import { UserProfile } from '@hiptraveler/data-access/api';
import { ProfileAction, ProfileState } from '@hiptraveler/data-access/profile';
import { UserSettingsService } from '../user-settings.service';
import { ProfileStateService, ProfileStateFormService, SocialMedia } from '../../../common';
import { SnackbarService } from '@hiptraveler/snackbar';

@Injectable()
export class ProfileFormService implements OnDestroy {

  selectedSocMed$$ = new BehaviorSubject<SocialMedia>('twitter');
  subscription$ = new Subject<void>();

  socMedFormControl = new FormControl('', [
    Validators.pattern(/https?:\/\/.+/)
  ]);

  socialFormObject: Record<SocialMedia, string> = {
    twitter: '',
    facebook: '',
    youtube: '',
    pinterest: '',
    instagram: '',
    tiktok: '',
    website: ''
  };

  constructor(
    private store: Store,
    private stateService: ProfileStateService,
    private stateFormService: ProfileStateFormService,
    private userSettings: UserSettingsService,
    private snackbar: SnackbarService
  ) {
    this.stateService.saveClicked$$.asObservable()
      .pipe(takeUntil(this.subscription$))
      .subscribe(this.submitListener.bind(this));
  }

  ngOnDestroy(): void {
    this.subscription$.next();
  }

  setInitialProfileFormData(): void {

    this.socMedFormControl.valueChanges.subscribe((url: string) => {
      this.socialFormObject[this.selectedSocMed$$.value] = url;    
    });

    this.store.select(ProfileState.profileDetails).pipe(
      filter(Boolean),
      tap((profile: UserProfile) => this.socMedFormControl.patchValue(profile.connectWith['twitter'])),
      take(1)
    ).subscribe();

    this.store.select(ProfileState.profileDetails).pipe(
      filter(Boolean),
      tap((profile: UserProfile) => this.stateFormService.profileForm.patchValue({
        firstName: profile.firstName,
        lastName: profile.lastName,
        userDesc: profile.description,
        travelQuote: profile.favQuote,
        homeTown: profile.hometown,
        currLoc: profile.userLoc,
        rStatus: profile.rStatus,
        dob: profile.dob ? new Date(profile.dob).toISOString() : ''
      })),
      take(1)
    ).subscribe();
  }

  get socMedLabel$(): Observable<string> {
    return this.selectedSocMed$$.asObservable().pipe(
      map((value: SocialMedia) => {
        switch (value) {
          case 'twitter': return 'profile.twitter';
          case 'facebook': return 'profile.facebook';
          case 'youtube': return 'profile.youtube';
          case 'pinterest': return 'profile.pinterest';
          case 'instagram': return 'profile.instagram';
          case 'tiktok': return 'profile.tiktok';
          case 'website': return 'profile.personal-site';
        }
      })
    )
  }

  updateSocialMediaInit(input: HTMLInputElement): void {
    setTimeout(() => {
      this.selectedSocMed$$.next('twitter');

      this.store.select(ProfileState.profileDetails).pipe(
        filter(Boolean),
        tap((profile: UserProfile) => {
          input.value = profile?.connectWith?.twitter || '';
        }),
        take(1)
      ).subscribe();
      
    });
  }

  updateSocialMedia(value: SocialMedia, input: HTMLInputElement): void {
    setTimeout(() => {
      this.selectedSocMed$$.next(value);
      const socialMediaMap = this.store.selectSnapshot(ProfileState.profileDetails)?.connectWith;
      switch (value) {
        case 'twitter': return (input.value = this.socialFormObject['twitter'] || socialMediaMap?.[value] || '');
        case 'facebook': return (input.value = this.socialFormObject['facebook'] || socialMediaMap?.[value] || '');
        case 'youtube': return (input.value = this.socialFormObject['youtube'] || socialMediaMap?.[value] || '');
        case 'pinterest': return (input.value = this.socialFormObject['pinterest'] || socialMediaMap?.[value] || '');
        case 'instagram': return (input.value = this.socialFormObject['instagram'] || socialMediaMap?.[value] || '');
        case 'tiktok': return (input.value = this.socialFormObject['tiktok'] || socialMediaMap?.[value] || '');
        case 'website': return (input.value = this.socialFormObject['website'] || socialMediaMap?.[value] || '');
      }
    });
  }

  private get hasInvalidConnectUrl(): boolean {
    for (const key of Object.keys(this.socialFormObject)) {
      const url = this.socialFormObject[key as SocialMedia];
      // only validate when there's a value
      if (url && !/^https?:\/\/.+/.test(url)) {
        return true;  // found an invalid URL
      }
    }
    return false;   // all URLs are valid (or empty)
  }

  async submitListener(): Promise<void> {
    const value = this.stateFormService.profileForm.value;
    value.dob = new Date(value.dob).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    value.dob = value.dob === 'Invalid Date' ? '' : value.dob;
    value.id = this.store.selectSnapshot(ProfileState.profileDetails)!.profileId;
    value.social = Object.keys(this.socialFormObject).map((keys) => ({
      social: keys,
      connect_url: this.socialFormObject[keys as SocialMedia]
    })).filter(e => e.connect_url);

    if (this.socMedFormControl.invalid || this.hasInvalidConnectUrl) {
      this.snackbar.open({ message: 'One or more social URLs are invalid.', duration: 5000 });
      return;
    }

    try {
      this.userSettings.formProcessing$$.next(true);
      await firstValueFrom(this.store.dispatch(new ProfileAction.UpdateProfileContent(value)));
      this.userSettings.formProcessing$$.next(false);
      this.stateService.profileTabSelection$$.next('about');
      this.stateService.profileSettingsView$$.next(false);
    } catch (error) {
    } finally {
    }
  }

}
